import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import React, {ReactElement, useState} from 'react';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import {IconButton} from '@mui/material';
import {hasRightToEdit} from '../../../../utils/role-to-rights';
import {PsfTemplateItemsEditorProps} from '../../../../@types/editor-props';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import ErrorAlertComponent from '../../../ErrorAlert.component';
import {PsfTemplateItem} from '../../../../@types/psfTemplate';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import PsfTemplateItemEditor from './PsfTemplateItemEditor';
import OkCancelModal from '../../../form-components/OkCancelModal.component';
import LaunchIcon from '@mui/icons-material/Launch';
import AreYouSureModal from '../../../form-components/AreYouSureModal.component';
import authService from '../../../../services/auth.service';

const initPsfItem: PsfTemplateItem = {
    category: '',
    name: '',
    code: '',
    description: '',
    examples: ''
};

export default function PsfTemplateItemsEditor(props: PsfTemplateItemsEditorProps): ReactElement {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { categoryIndex, psfCategories, updateCategory, isActive, updateTab } = props;
    const [currentFactor, setCurrentFactor] = React.useState<PsfTemplateItem>(initPsfItem);
    const [editingFactor, setEditingFactor] = React.useState<PsfTemplateItem|null>(null);
    const [currentIndex, setCurrentIndex] = React.useState<number>(0);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [error, setError] = useState<string|null>(null);
    const [confirmAddOrUpdatePsfItem, setConfirmAddOrUpdatePsfItem] = useState<boolean>(false);
    const [confirmDeletePsfItem, setConfirmDeletePsfItem] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { currentWorkspaceObject, setCurrentWorkspaceObject } = useWorkspaceContext();

    React.useEffect(() => {
        if (categoryIndex >= 0 && psfCategories[categoryIndex]) {
            setCurrentFactor({...currentFactor, category: psfCategories[categoryIndex].path });
        }
    }, [psfCategories, categoryIndex])

    const addOrUpdateFactor = (psfItem: PsfTemplateItem, index?: number): void => {
        let itemIndex = index ?? currentIndex;
        if (!psfCategories[categoryIndex].psfTemplateItems) {
            psfCategories[categoryIndex].psfTemplateItems = [] as PsfTemplateItem[];
        }
        if (itemIndex && itemIndex > -1) {
            psfCategories[categoryIndex]!.psfTemplateItems![itemIndex] = psfItem;
        } else {
            psfCategories[categoryIndex]!.psfTemplateItems!.push(psfItem);
        }
        updateCategory(psfCategories[categoryIndex]);
    };


    const isNotEditable = (): boolean => {
        return currentWorkspaceObject ? currentWorkspaceObject.object?.id !== null && currentWorkspaceObject.object?.id !== undefined &&
            !hasRightToEdit(currentWorkspaceObject, authService.isSuperUser()) : true;
    }
    
    const removeItem = (index: number): void => {
        psfCategories[categoryIndex].psfTemplateItems?.splice(index, 1);
        updateCategory(psfCategories[categoryIndex]);
    }

    const handleAddItem = (): void => {
        setCurrentFactor({...initPsfItem, category: psfCategories[categoryIndex].path});
        setEditingFactor({...initPsfItem, category: psfCategories[categoryIndex].path});
        setCurrentIndex(-1);
        setConfirmAddOrUpdatePsfItem(true);
    }

    const getExistingFactors = (): PsfTemplateItem[] => {
        return psfCategories[categoryIndex]?.psfTemplateItems?.filter((item) => item.name !== currentFactor.name) ?? [];
    }

    return (
        <>
            <Box sx={{height: '600px', overflowX: 'hidden', overflowY: 'auto', flexGrow: '1' }}>
                <Box sx={{ width: '100%' }}>
                    <Stack spacing={2} direction="column" justifyContent='space-evenly'>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}>
                            <Typography variant="h6">{psfCategories[categoryIndex]?.name}</Typography>
                            <Button
                                variant='contained'
                                title='Add New Factor'
                                component='button'
                                style={{ textTransform: 'none' }}
                                onClick={handleAddItem}
                                startIcon={<AddIcon />}
                                disabled={isNotEditable() || psfCategories.findIndex((item) => item.name === '') >= 0}
                            >Add New Factor</Button>
                        </Box>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                <TableBody>
                                    {psfCategories[categoryIndex]?.psfTemplateItems?.map((item: PsfTemplateItem, index: number) => <TableRow key={item.name + '-' + index}><TableCell style={{ width: '80%'}}>
                                        <PsfTemplateItemEditor factor={item} updateFactor={ (factor): void => addOrUpdateFactor(factor, index) }
                                            readonly={isNotEditable()}
                                            existingFactors={(psfCategories[categoryIndex].psfTemplateItems?.filter((psfItem) => item.name !== psfItem.name) ?? [])}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton color='primary'
                                            onClick={(): void => {
                                                setCurrentFactor(item);
                                                setEditingFactor(item);
                                                setCurrentIndex(index);
                                                setConfirmAddOrUpdatePsfItem(true);
                                            }}
                                            aria-label='Popup Factor'>
                                            <LaunchIcon />
                                        </IconButton>
                                        <IconButton color='primary'
                                            onClick={(): void => {
                                                setCurrentFactor(item);
                                                setCurrentIndex(index);
                                                setConfirmDeletePsfItem(true);
                                            }}
                                            disabled={isNotEditable()}
                                            aria-label='Remove Item'>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                    </TableRow>)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {error ? <ErrorAlertComponent message={error} />  : null}
                    </Stack>
                </Box>
            </Box>
            <OkCancelModal
                open={confirmAddOrUpdatePsfItem}
                title={'Factor:'}
                onClose={(): void => { setConfirmAddOrUpdatePsfItem(false); }}
                okButtonText='OK'
                onConfirm={(): void => { addOrUpdateFactor(editingFactor!, currentIndex); setConfirmAddOrUpdatePsfItem(false); }}
                okToConfirm={(): boolean => { return !isNotEditable() && editingFactor?.name?.trim() !== ''; }}
                maxWidth={'md'}
            >
                <PsfTemplateItemEditor
                    factor={editingFactor ?? currentFactor} updateFactor={setEditingFactor} readonly={isNotEditable()}
                    existingFactors={getExistingFactors()} textMaxRows={10} textMinRows={5}
                />
            </OkCancelModal>
            <AreYouSureModal
                open={confirmDeletePsfItem}
                title={`Delete Factor - "${currentFactor.name}"?`}
                onConfirm={(): void => { removeItem(currentIndex); setConfirmDeletePsfItem(false); }}
                onClose={(): void => {
                    setConfirmDeletePsfItem(false);
                }}
            >
                <Typography variant='body1'>Are you sure you want to delete this item?</Typography>
                <Typography variant='body2'>Note: This will delete any and all associated presences.</Typography>
            </AreYouSureModal>
        </>
    );
}